import { useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { getListEntriesByFilterParam } from 'Services';
import _ from 'lodash';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useSingleList404Modal from 'Hooks/reduxHelperHooks/useSingleList404Modal';
import { useTranslation } from 'react-i18next';
import { RQ_LIST_ENTRIES } from 'Utils';
import { LIST_TYPES } from 'Common/enums';
import { ListEntryApiResponseObj, ListEntryFilterResponseObj } from '@brighterion/types/listEntry';
import { addFormattedPropertyEntry } from '../list/util';

interface InfiniteResponse extends Omit<UseInfiniteQueryResult<ListEntryFilterResponseObj, unknown>, 'data'> {
    data?: {
        result: ListEntryApiResponseObj[];
        totalCount: number;
    };
}

type Props = {
    listId: number;
    listType: SingleListType;
    filterParam: string;
    statusParam: string;
    enabled?: boolean;
    users?: UsersHashmap;
};

const useGetListEntriesByFilterParam = ({
    listId,
    listType,
    filterParam,
    statusParam,
    enabled = false,
    users
}: Props) => {
    const exact = listType !== LIST_TYPES.GENERAL;
    const { t } = useTranslation();
    const { addTechnicalErrorModal } = useReduxModal();
    const { listNotFound } = useSingleList404Modal();

    return (useInfiniteQuery(
        [RQ_LIST_ENTRIES, listId, filterParam],
        ({ pageParam = 1 }) => getListEntriesByFilterParam(listId, exact, filterParam, pageParam, statusParam),
        {
            getNextPageParam: (lastPage, pages) => {
                const lastPageTotal = lastPage.totalCount;
                const totalCount = _.reduce(
                    pages,
                    (total, n) => {
                        return total + n.numResult;
                    },
                    0
                );
                return totalCount < lastPageTotal ? pages.length + 1 : undefined;
            },
            select: (data): any => {
                const { pages } = data as any;
                const totalPages = _.reduce(
                    pages,
                    (result: any, page: any) => {
                        result.push(...page.result);
                        return result;
                    },
                    []
                );
                const { totalCount } = pages[pages.length - 1];
                return {
                    result: totalPages.map(addFormattedPropertyEntry(t, users)),
                    totalCount
                };
            },
            enabled: enabled !== false,
            onError: (error: any) => {
                switch (error?.status) {
                    case 404: {
                        listNotFound();
                        break;
                    }
                    default:
                        addTechnicalErrorModal();
                        break;
                }
            }
        }
    ) as unknown) as InfiniteResponse;
};

export default useGetListEntriesByFilterParam;
